import { useState } from 'react'
import { Link } from 'react-router-dom'

import { RoundBadge, RoundsTable } from '../components/rounds.tsx'
import { Banner, Card, CardBody, CardHead, LoadError, PageHead, Seg, Skeleton, Stat } from '../components/ui.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { post, useApi } from '../lib/api.ts'
import { moneyIn, pnl, setCurrency } from '../lib/format.ts'
import { CURRENCIES, ROUND_BANKROLL, type BankrollPayload, type Currency } from '../../shared/types.ts'

/**
 * The player's play bankroll. Every round starts at 10,000 in the currency they choose;
 * asking for a fresh 10,000 starts a new round. Every round stays on the record — busts
 * included — and other players can see it on the leaderboard.
 */
export function Bankroll() {
  usePageTitle('Bankroll')
  const { data, error, loading, reload } = useApi<BankrollPayload>('/bankroll', 30_000)
  const [currency, setChoice] = useState<Currency | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>
  if (loading || !data) {
    return (
      <main className="content">
        <Card>
          <CardBody>
            <Skeleton rows={8} />
          </CardBody>
        </Card>
      </main>
    )
  }

  const c = data.current
  const chosen = currency ?? data.currency
  const money = (n: number) => moneyIn(n, c.currency)
  const result = c.balance - c.startingBalance

  const start = async () => {
    setBusy(true)
    setErr(null)
    try {
      const res = await post<BankrollPayload>('/bankroll/rounds', { currency: chosen })
      setCurrency(res.current.currency)
      setConfirming(false)
      setChoice(null)
      reload()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="content">
      <PageHead
        title="Bankroll"
        sub={`Every round starts with ${ROUND_BANKROLL.toLocaleString('en-GB')} to bet with. Ask for a fresh one whenever you like — each starts a new round.`}
        actions={
          <Link className="btn ghost sm" to="/leaderboard">
            Leaderboard →
          </Link>
        }
      />

      <div className="stack">
        {c.status === 'bust' && (
          <Banner tone="halt" title={`Round ${c.number} went to zero`}>
            It stays on your record. Start a new round below for a fresh {money(ROUND_BANKROLL)}.
          </Banner>
        )}

        <div className="grid cols-4">
          <Card pad>
            <Stat label={`Round ${c.number} balance`} value={money(c.balance)} sub={`Started with ${money(c.startingBalance)}`} large />
          </Card>
          <Card pad>
            <Stat label="Available to bet" value={money(c.available)} sub="Balance less what open bets risk" />
          </Card>
          <Card pad>
            <Stat label="At risk" value={money(c.atRisk)} sub={`${c.openBets} open bet${c.openBets === 1 ? '' : 's'}`} />
          </Card>
          <Card pad>
            <Stat
              label="This round"
              tone={result > 0.005 ? 'pos' : result < -0.005 ? 'neg' : null}
              value={pnl(result, c.currency)}
              sub={`${c.settledBets} settled bet${c.settledBets === 1 ? '' : 's'}`}
            />
          </Card>
        </div>

        <Card>
          <CardHead title="Start a new round" right={<RoundBadge status={c.status} />} />
          <CardBody className="stack sm">
            <div className="u-faint">
              Round {c.number} closes and round {c.number + 1} starts now with {moneyIn(ROUND_BANKROLL, chosen)}. Bets you
              have open still settle into round {c.number}. Rounds can never be deleted — anyone on the leaderboard can see
              how each of yours ended.
            </div>
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Seg
                label="Currency"
                value={chosen}
                onChange={(v) => {
                  setChoice(v)
                  setConfirming(false)
                }}
                options={CURRENCIES.map((k) => ({ value: k, label: k }))}
              />
              {confirming ? (
                <>
                  <button className="btn primary" disabled={busy} onClick={start}>
                    {busy ? 'Starting…' : `Yes, start round ${c.number + 1}`}
                  </button>
                  <button className="btn ghost" disabled={busy} onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="btn primary" onClick={() => setConfirming(true)}>
                  Get a fresh {moneyIn(ROUND_BANKROLL, chosen)}
                </button>
              )}
            </div>
            {err && (
              <span className="help warn" role="alert">
                {err}
              </span>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHead title="Your rounds" sub={`${data.rounds.length} so far · ${data.rounds.filter((r) => r.status === 'bust').length} went to zero`} />
          <CardBody flush>
            <RoundsTable rounds={data.rounds} ownBets />
          </CardBody>
        </Card>
      </div>
    </main>
  )
}
